import { useCallback, useEffect, useState } from 'react';

import useWidgetRouterStore from '../internals/widgetRouterStore';

/**
 * useProcedurePrefillBridge — captures `sdk.openWidget('procedure', ...)`
 * activations and routes the prefill into the procedure rail.
 *
 * Concerns owned here (kept out of `ProcedureWidget` so its body stays
 * focused on layout + size + sittings list):
 *   1. Visit-scoping  — ignore activations targeting a different visit.
 *   2. Placement      — when `opts.placement === 'chart-pane'`, the
 *                       chart owns the picker mount (renders inline in
 *                       its pane). The rail must NOT expand and the
 *                       store must NOT be closed here — the chart's own
 *                       SDK subscriber will consume and close.
 *   3. Rail expansion — for default ('rail') placement, expand the rail
 *                       to 'focused' so `QuickSearchAdd` mounts and
 *                       receives the prefill.
 *   4. Race defense   — every activation carries a `requestId` (UUID).
 *                       The hook tracks it so rapid successive calls
 *                       trigger fresh re-applications of the prefill
 *                       even if other fields are unchanged.
 *
 * Returns `{ pendingPrefill, clearPending }`:
 *   - `pendingPrefill` — `{ tokens, diagnosisMetaId, diagnosisSummary,
 *     requestId } | null`. Forward to QuickSearchAdd as the
 *     `prefilled*` props.
 *   - `clearPending`   — call after `QuickSearchAdd.onCreated` to free
 *     the slot for the next activation.
 */
export default function useProcedurePrefillBridge({ visitId, setSize }) {
  const activeWidget = useWidgetRouterStore((s) => s.active);
  const closeWidget  = useWidgetRouterStore((s) => s.closeWidget);
  const [pendingPrefill, setPendingPrefill] = useState(null);

  useEffect(() => {
    if (!activeWidget || activeWidget.kind !== 'procedure') return;
    if (!visitId) return;
    if (activeWidget.context?.visitId && activeWidget.context.visitId !== visitId) return;

    // Chart-pane placement: the chart will host the picker inline. The
    // rail stays out of it — no expand, no store close, no prefill
    // capture here. The chart's own SDK subscriber handles everything.
    if (activeWidget.opts?.placement === 'chart-pane') return;

    const p = activeWidget.context?.prefill || {};
    setPendingPrefill({
      tokens:           Array.isArray(p.tokens) ? p.tokens : null,
      diagnosisMetaId:  p.diagnosisMetaId || null,
      diagnosisSummary: p.summary || null,
      // Phase 3 — drag-drop carries the procedure id so QuickSearchAdd
      // can auto-select instead of making the dentist type/search again.
      procedureId:      p.procedureId || null,
      requestId:        p.requestId || `auto-${Date.now()}`,
    });
    setSize('focused');
    closeWidget();
  }, [activeWidget, visitId, setSize, closeWidget]);

  const clearPending = useCallback(() => setPendingPrefill(null), []);

  return { pendingPrefill, clearPending };
}
